/**
 * keyboard.js — soft-keyboard visibility abstraction.
 *
 * Emits a single `visible` boolean when the on-screen keyboard opens
 * or closes so a component can slide sticky action bars above it
 * without doing manual visualViewport math per screen. The web
 * fallback uses `window.visualViewport` (Chromium / iOS Safari) and
 * falls back to `focus`/`blur` bubbling on form controls.
 *
 * Consumers should treat "visible" as a transient UI hint. Do NOT
 * couple business logic to it — a Bluetooth keyboard on tablet, an
 * OS voice-input overlay, or predictive-text bar can all trigger
 * unexpected transitions.
 */
import { isNative } from './platform.js';

let _keyboard = null;

async function keyboardPlugin() {
  if (_keyboard) return _keyboard;
  const mod = await import('@capacitor/keyboard');
  _keyboard = mod.Keyboard;
  return _keyboard;
}

/**
 * Subscribe to keyboard visibility. Handler receives `{ visible, height }`
 * where `height` is the CSS-pixel height when known, or 0 on close.
 */
export function onVisibilityChange(handler) {
  if (typeof handler !== 'function') return () => {};

  if (isNative()) {
    let cleanup = () => {};
    let cancelled = false;
    (async () => {
      try {
        const kb = await keyboardPlugin();
        const showSub = await kb.addListener('keyboardWillShow', (info) => {
          handler({ visible: true, height: info?.keyboardHeight || 0 });
        });
        const hideSub = await kb.addListener('keyboardWillHide', () => {
          handler({ visible: false, height: 0 });
        });
        if (cancelled) {
          try { showSub.remove(); hideSub.remove(); } catch { /* ignore */ }
          return;
        }
        cleanup = () => {
          try { showSub.remove(); } catch { /* ignore */ }
          try { hideSub.remove(); } catch { /* ignore */ }
        };
      } catch { /* plugin missing */ }
    })();
    return () => { cancelled = true; cleanup(); };
  }

  // Web fallback — visualViewport is the most reliable signal for the
  // iOS Safari soft keyboard. When it isn't available (older browsers,
  // desktop), we return a no-op so no false-positives fire.
  if (typeof window === 'undefined' || !window.visualViewport) {
    return () => {};
  }
  const vv = window.visualViewport;
  let lastVisible = false;
  const emit = () => {
    // A keyboard is likely present when the visual viewport is
    // materially shorter than the layout viewport.
    const layoutH = window.innerHeight || document.documentElement.clientHeight;
    const visualH = vv.height;
    const delta = Math.max(0, layoutH - visualH);
    const visible = delta > 120; // heuristic; matches Android + iOS
    if (visible !== lastVisible) {
      lastVisible = visible;
      handler({ visible, height: visible ? delta : 0 });
    }
  };
  vv.addEventListener('resize', emit);
  vv.addEventListener('scroll', emit);
  return () => {
    vv.removeEventListener('resize', emit);
    vv.removeEventListener('scroll', emit);
  };
}

/** Ask the OS to dismiss the keyboard. Best-effort on both platforms. */
export async function hide() {
  if (!isNative()) {
    try { document.activeElement?.blur?.(); } catch { /* ignore */ }
    return;
  }
  try { const kb = await keyboardPlugin(); await kb.hide(); } catch { /* ignore */ }
}
